// src/components/NotificationCenter.js
// Bell icon with dropdown panel listing actionable system notifications.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiAlertCircle, FiAlertTriangle, FiInfo, FiCheck } from 'react-icons/fi';
import { t, useLanguage } from '../useTranslations';
import notificationService from '../services/NotificationService';
import '../styles/NotificationCenter.css';

const CATEGORY_ICON = {
  urgent: FiAlertCircle,
  warning: FiAlertTriangle,
  info: FiInfo,
};

const NotificationCenter = () => {
  useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef(null);

  const refresh = useCallback(() => {
    setNotifications(notificationService.getNotifications());
    setUnreadCount(notificationService.getUnreadCount());
  }, []);

  useEffect(() => {
    refresh();
    const unsub = notificationService.subscribe(refresh);
    return unsub;
  }, [refresh]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen(prev => !prev);
  };

  const handleNotificationClick = useCallback((notification) => {
    notificationService.markAsRead(notification.id);
    setOpen(false);
    navigate(notification.route);
  }, [navigate]);

  const handleMarkAllRead = (e) => {
    e.stopPropagation();
    notificationService.markAllAsRead();
  };

  const urgentCount = notifications.filter(n => n.category === 'urgent').length;
  const warningCount = notifications.filter(n => n.category === 'warning').length;

  return (
    <div className="notification-center" ref={panelRef}>
      <button
        className={`notification-bell-button${unreadCount > 0 ? ' has-unread' : ''}`}
        onClick={handleToggle}
        aria-label={t('notifications.label')}
        title={t('notifications.label')}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <FiBell size={16} />
        {unreadCount > 0 && (
          <span className={`notification-badge${urgentCount > 0 ? ' urgent' : ''}`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-panel" role="dialog" aria-label={t('notifications.panelTitle')}>
          <div className="notification-panel-header">
            <span className="notification-panel-title">{t('notifications.panelTitle')}</span>
            {unreadCount > 0 && (
              <button className="notification-mark-all-btn" onClick={handleMarkAllRead}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="notification-empty">
              <FiCheck size={20} />
              <span>{t('notifications.empty')}</span>
            </div>
          ) : (
            <div className="notification-list">
              {notifications.map(n => {
                const Icon = CATEGORY_ICON[n.category] || FiInfo;
                return (
                  <button
                    key={n.id}
                    className={`notification-item notification-item--${n.category}${n.read ? ' read' : ''}`}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <span className="notification-item-icon">
                      <Icon size={14} />
                    </span>
                    <span className="notification-item-body">
                      <span className="notification-item-title">{t(n.titleKey, n.titleParams)}</span>
                      <span className="notification-item-desc">{t(n.descKey, n.descParams)}</span>
                    </span>
                    {!n.read && (
                      <span className="notification-unread-dot" aria-label={t('notifications.unread')} />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {notifications.length > 0 && (
            <div className="notification-panel-footer">
              {urgentCount > 0 && (
                <span className="notification-summary-urgent">
                  {t('notifications.urgentCount', { count: urgentCount })}
                </span>
              )}
              {warningCount > 0 && (
                <span className="notification-summary-warning">
                  {t('notifications.warningCount', { count: warningCount })}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
